import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { BarChart2, ArrowLeft } from "lucide-react";
import { GBPPerformanceProps, DateRange } from "./types/PerformanceTypes";
import { TABS, TabId } from "../../constants/constants";
import { todayStr, daysAgo } from "./utils/PerformanceUtils";
import Spinner    from "./ui/Spinner";
import ErrBox     from "./ui/ErrBox";
import Empty      from "./ui/Empty";
import OverviewTab  from "./tabs/OverviewTab";
import TrafficTab   from "./tabs/TrafficTab";
import DevicesTab   from "./tabs/DevicesTab";
import ActionsTab   from "./tabs/ActionsTab";
import KeywordsTab  from "./tabs/KeywordsTab";
import TrendsTab    from "./tabs/TrendsTab";
import ReviewsTab   from "./tabs/ReviewsTab";
import GBPHeader, { CompareRangeRow, shiftRangeBack, formatDateLabel } from "./components/Gbpheader";
import { useGBPData, normalizeReviews } from "../../hooks/useGBPData";
import { useGBPExport } from "../../hooks/useGBPExport";
 const TabBtn: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center gap-1.5 px-1 h-[48px] text-[13px] font-medium
      border-none bg-transparent cursor-pointer whitespace-nowrap tracking-tight
      border-b-2 transition-all duration-150 mr-5
      ${active
        ? "text-[#0F52BA] border-[#0F52BA] font-semibold"
        : "text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-200"
      }
    `}
  >
    <span className={`transition-colors ${active ? "text-[#0F52BA]" : "text-slate-400"}`}>
      {icon}
    </span>
    {label}
  </button>
);

 const GBPPerformance: React.FC<GBPPerformanceProps> = ({
  locationName, accountName, ficheName, subjectEmail, onClose, onNavigateHome,
}) => {
  const [activeTab,      setActiveTab]      = useState<TabId>("overview");
  const [dateRange,      setDateRange]      = useState<DateRange>({ startDate: daysAgo(29), endDate: todayStr() });
  const [activePeriod,   setActivePeriod]   = useState<number | null>(30);
  const [compareEnabled,    setCompareEnabled]    = useState(false);
  const [compareRange,      setCompareRange]      = useState<DateRange>(() =>
    shiftRangeBack({ startDate: daysAgo(29), endDate: todayStr() })
  );
   const [userEditedCompare, setUserEditedCompare] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  const modalRef    = useRef<HTMLDivElement>(null);
  const isRegion    = !!accountName && !locationName;
  const hasLocation = !!locationName || !!accountName;
  const isModal     = !!onClose;
    
   useEffect(() => {
    if (compareEnabled && !userEditedCompare) {
      setCompareRange(shiftRangeBack(dateRange));
    }
  }, [dateRange, compareEnabled, userEditedCompare]);

  useEffect(() => {
    if (!isModal || !onClose) return;
    const fn = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [isModal, onClose]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (locationName) p.append("locationName", locationName);
    if (accountName)  p.append("accountName",  accountName);
    if (subjectEmail)  p.append("subjectEmail", subjectEmail); 

    p.append("startDate", dateRange.startDate);
    p.append("endDate",   dateRange.endDate);
    return p.toString();
  }, [locationName, accountName, subjectEmail, dateRange]);

 const qsCompare = useMemo(() => {
  if (!compareEnabled) return null;
  const p = new URLSearchParams();
  if (locationName) p.append("locationName", locationName);
  if (accountName)  p.append("accountName",  accountName);
  if (subjectEmail) p.append("subjectEmail", subjectEmail);
  p.append("startDate", compareRange.startDate);
  p.append("endDate",   compareRange.endDate);
  return p.toString();
}, [locationName, accountName, subjectEmail, compareRange, compareEnabled]);

  const {
    ficheFull, ficheFull2,
    regionOverview, regionOverview2, regionReviews, regionData, regionData2,
    ficheData, ficheData2,
  } = useGBPData({ locationName, accountName, qs, qsCompare, hasLocation, isRegion, activeTab });

  const primaryPeriodLabel = `${formatDateLabel(dateRange.startDate)} – ${formatDateLabel(dateRange.endDate)}`;
  const comparePeriodLabel = `${formatDateLabel(compareRange.startDate)} – ${formatDateLabel(compareRange.endDate)}`;
  const compareProps = compareEnabled ? { compareEnabled: true, primaryPeriodLabel, comparePeriodLabel } : {};

  const { exporting, exportCSV, exportReport } = useGBPExport({
    ficheName, locationName, dateRange,
    compareEnabled, compareRange,
    primaryPeriodLabel:  compareEnabled ? primaryPeriodLabel : undefined,
    comparePeriodLabel:  compareEnabled ? comparePeriodLabel : undefined,
    ficheFull:     ficheFull.data,
    ficheFull2:    ficheFull2.data,
    regionData, regionData2,
    regionReviews: regionReviews.data,
    isRegion,
  });

const handleQuickRange = (days: number) => {
  const r = { startDate: daysAgo(days - 1), endDate: todayStr() };
  setDateRange(r);
  setActivePeriod(days);
  setUserEditedCompare(false);
  setIsReloading(true);  
};

const handleDateChange = (r: DateRange) => {
  setDateRange(r);
  setActivePeriod(null);
  setUserEditedCompare(false);
  setIsReloading(true); 
};

const handleCompareRangeChange = (r: DateRange) => {
  setCompareRange(r);
  setUserEditedCompare(true);
  setIsReloading(true);  
};

 useEffect(() => {
  const anyFetching =
    ficheFull.isFetching       ||
    ficheFull2.isFetching      ||
    regionOverview.isFetching  ||
    regionOverview2.isFetching  ||
    regionReviews.isFetching;
  if (isReloading && !anyFetching) {
    setIsReloading(false);
  }
}, [
  isReloading,  
  ficheFull.isFetching,
  ficheFull2.isFetching,
  regionOverview.isFetching,
   regionOverview2.isFetching,
  regionReviews.isFetching,
]);
const handleToggleCompare = () => {
  const next = !compareEnabled;
  setCompareEnabled(next);
  if (next) {
    setUserEditedCompare(false);
    setCompareRange(shiftRangeBack(dateRange));
    setIsReloading(true); 
  }
};
 

  const activeQuery = isRegion
    ? (activeTab === "reviews" ? regionReviews : regionOverview)
    : ficheFull;
        

  const dataReady = !activeQuery.isLoading && !activeQuery.isError;

   const renderContent = () => {
    if (!hasLocation) return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-4 opacity-50">
          <BarChart2 size={26} className="text-slate-400" />
        </div>
        <p className="text-sm font-semibold text-slate-600 mb-1">Aucune fiche sélectionnée</p>
        <p className="text-xs text-slate-400 mb-5">Sélectionnez une fiche GBP pour afficher ses performances.</p>
        {onNavigateHome && (
          <button
            onClick={onNavigateHome}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0F52BA] text-white text-sm font-semibold border-none cursor-pointer hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200"
          >
            <ArrowLeft size={14} /> Retour
          </button>
        )}
      </div>
    );

    if (activeQuery.isLoading) return <Spinner />;
    if (activeQuery.isError)   return (
      <ErrBox msg={(activeQuery.error as any)?.response?.data?.message || "Erreur lors du chargement"} />
    );

    if (isRegion) {
      if (activeTab === "reviews") {
        const rd = regionReviews.data;
        return rd
          ? <ReviewsTab data={normalizeReviews(rd)} {...compareProps}
              compareData={compareEnabled && rd ? normalizeReviews(rd) : undefined} />
          : <Empty />;
      }
      if (activeTab === "keywords") return <Empty />;
      if (!regionData) return <Empty />;
      const rd2 = compareEnabled ? regionData2 : null;
      switch (activeTab) {
        case "overview": return <OverviewTab data={regionData.overviewData} {...compareProps} compareData={rd2?.overviewData} />;
        case "traffic":  return <TrafficTab  data={regionData.trafficData}  {...compareProps} compareData={rd2?.trafficData} />;
        case "devices":  return <DevicesTab  data={regionData.devicesData}  {...compareProps} compareData={rd2?.devicesData} />;
        case "actions":  return <ActionsTab  data={regionData.actionsData}  {...compareProps} compareData={rd2?.actionsData} />;
        case "trends":   return <TrendsTab   data={regionData.trendsData}   {...compareProps} compareData={rd2?.trendsData} />;
        default: return null;
      }
    }

    const d = ficheFull.data;
    if (!d || !ficheData) return <Empty />;
    const d2   = ficheFull2.data;
    const cmp2 = compareEnabled && d2 && ficheData2 ? {
      kpis: d2.kpis, series: d2.series, devices: d2.devices,
      keywords: d2.keywords, trends: d2.trends,
      trafficData: ficheData2.trafficData, actionsData: ficheData2.actionsData, reviewsData: ficheData2.reviewsData,
    } : null;

    switch (activeTab) {
      case "overview": return <OverviewTab data={{ kpis: d.kpis, series: d.series }} {...compareProps} compareData={cmp2 ? { kpis: cmp2.kpis, series: cmp2.series } : undefined} />;
      case "traffic":  return <TrafficTab  data={ficheData.trafficData}  {...compareProps} compareData={cmp2?.trafficData} />;
      case "devices":  return <DevicesTab  data={d.devices}              {...compareProps} compareData={cmp2?.devices} />;
      case "actions":  return <ActionsTab  data={ficheData.actionsData}  {...compareProps} compareData={cmp2?.actionsData} />;
      case "keywords": return <KeywordsTab data={d.keywords}             {...compareProps} compareData={cmp2?.keywords} />;
      case "trends":   return <TrendsTab   data={d.trends}               {...compareProps} compareData={cmp2?.trends} />;
      case "reviews":  return <ReviewsTab  data={ficheData.reviewsData}  {...compareProps} compareData={cmp2?.reviewsData} />;
      default: return null;
    }
  };

   const panel = (
    <div
      ref={modalRef}
      className={`
        flex flex-col bg-white w-full overflow-hidden
        ${isModal
          ? "rounded-2xl max-w-[1100px] max-h-[calc(100vh-48px)] shadow-[0_24px_64px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.06)] animate-[gbpFadeIn_0.22s_ease]"
          : "h-full rounded-none shadow-none"
        }
      `}
    >
       <GBPHeader
        ficheName={ficheName}
        locationName={locationName}
        hasLocation={hasLocation}
        dateRange={dateRange}
        activePeriod={activePeriod}
        compareEnabled={compareEnabled}
        exporting={exporting}
        dataReady={dataReady}
        onDateChange={handleDateChange}
        onQuickRange={handleQuickRange}
        onToggleCompare={handleToggleCompare}
        onExportCSV={exportCSV}
        onExportReport={exportReport}
        onClose={onClose}
        onNavigateHome={onNavigateHome}
      />

      {hasLocation && compareEnabled && (
        <CompareRangeRow primary={dateRange} compare={compareRange} onChange={handleCompareRangeChange} />
      )}

       <div className="flex px-6 bg-white border-b border-slate-100 overflow-x-auto flex-shrink-0 [&::-webkit-scrollbar]:h-0">
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          const Icon   = tab.icon;
          return (
            <TabBtn
              key={tab.id}
              active={active}
              onClick={() => setActiveTab(tab.id as TabId)}
              icon={<Icon size={14} strokeWidth={active ? 2.25 : 1.75} />}
              label={tab.label}
            />
          );
        })}
      </div>
{isReloading && (
  <div className="flex items-center justify-center gap-2 py-3 bg-blue-50/60
    border-b border-blue-100 text-[11px] font-medium text-blue-600">
    <div className="w-3.5 h-3.5 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin" />
    Mise à jour des données...
  </div>
)}
       <div className="flex-1 overflow-y-auto p-6 bg-slate-50/40
        [&::-webkit-scrollbar]:w-[4px]
        [&::-webkit-scrollbar-track]:bg-transparent
        [&::-webkit-scrollbar-thumb]:bg-slate-200
        [&::-webkit-scrollbar-thumb]:rounded-full
        [&::-webkit-scrollbar-thumb:hover]:bg-slate-300">
        {renderContent()}
      </div>
    </div>
  );

  if (isModal) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[1000] flex items-start justify-center px-4 py-6 overflow-y-auto bg-slate-900/60 backdrop-blur-sm">
        {panel}
      </div>,
      document.body
    );
  }

  return <div className="h-full flex flex-col">{panel}</div>;
};

export default GBPPerformance;